import { IUserFormData } from "@/types/UserTypes";
import { AuthClient, setToken, V1BEClient } from "@/clients";
import { useAuthStore } from "@/store/authStore";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";

export const useAuthService = () => {
  const router = useRouterState();
  const setIsLoggedIn = useAuthStore((state) => state.setIsLoggedIn);
  const setUserDetails = useAuthStore((state) => state.setUserDetails);
  const userDetails = useAuthStore((state) => state.userDetails);
  const setIsTokenChecked = useAuthStore((state) => state.setIsTokenChecked);
  const setErrMsg = useAuthStore.getState().setErrMsg;
  const setUserEvents = useAuthStore((state) => state.setUserEvents);
  const setHasEvents = useAuthStore((state) => state.setHasEvents);
  const navigate = useNavigate();

  const setAsLoggedOut = useCallback(() => {
    setIsLoggedIn(false);
    localStorage.removeItem("access_token");
    setToken("");
    setUserEvents([]);
    setHasEvents(false);
  }, [setIsLoggedIn, setUserEvents, setHasEvents]);

  const checkUserEvents = useCallback(async () => {
    const userDetails = useAuthStore.getState().userDetails;

    if (!userDetails.user_id || userDetails.user_type === 3) {
      setHasEvents(true);
      return;
    }

    try {
      const events = await V1BEClient.fetchMyEvents();
      setUserEvents(events.data);
      setHasEvents(events.data.length > 0);
    } catch (error) {
      console.error("Error checking user events:", error);
      setUserEvents([]);
      setHasEvents(false);
    }
  }, [setUserEvents, setHasEvents]);

  const handleCheckIfLoggedIn = useCallback(async () => {
    // Check if we already have a token in localStorage
    const token = localStorage.getItem("access_token");
    if (token) {
      setToken(token);
    } else {
      // No token, set as logged out immediately
      setAsLoggedOut();
      setIsTokenChecked(true);
      return;
    }

    try {
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Request timeout")), 10000)
      );

      const introspect = (await Promise.race([
        AuthClient.getIntrospect(),
        timeoutPromise,
      ])) as any;

      if (introspect) {
        setIsLoggedIn(true);
        const isVerified = introspect?.verification?.status === "verified";
        const isSupplier = introspect.user_type === 3;
        setUserDetails({
          ...introspect,
          is_supplier: isSupplier,
          is_verified: isVerified,
        });

        // Check for user events after successful authentication
        // await checkUserEvents();
      }

      setIsTokenChecked(true);
    } catch (err) {
      console.error("Auth check failed:", err);
      setAsLoggedOut();
      setIsTokenChecked(true);
    }
  }, [setIsLoggedIn, setUserDetails, setIsTokenChecked, setAsLoggedOut]);

  const handleLogout = useCallback(async () => {
    try {
      setAsLoggedOut();
      navigate({
        to: "/login",
      });
    } catch (error: any) {
      setErrMsg(error.response?.data?.message || "Logout failed");
    }
  }, [setAsLoggedOut, navigate, setErrMsg]);

  const handleSignIn = useCallback(
    async (data: IUserFormData, redirectTo: string = "/") => {
      setErrMsg("");
      try {
        const loginData = await AuthClient.signIn(data);
        if (loginData) {
          setIsLoggedIn(true);
          setUserDetails(loginData.user);
          setToken(loginData.access_token);

          navigate({
            to: redirectTo,
          });
        } else {
          setErrMsg("Invalid email or password");
        }
      } catch (err: any) {
        if (err.response?.status === 409) {
          setErrMsg("User already taken");
        } else if ([401, 403].includes(err.response?.status)) {
          setErrMsg(
            err.response?.data?.error?.split(": ")[1]?.trim() ||
            "Invalid credentials"
          );
        } else {
          setErrMsg("Invalid email or password");
        }
        setIsLoggedIn(false);
      }
    },
    [setErrMsg, setIsLoggedIn, setUserDetails, navigate]
  );

  const handleOAuthSignIn = useCallback(
    async (
      token: string,
      provider: string,
      isSignup: boolean = false,
      userType: number | null = null,
      redirectTo: string = "/"
    ) => {
      setErrMsg("");
      try {
        const loginData = await AuthClient.signInWithOAuth(
          token,
          provider,
          isSignup,
          userType
        );
        if (loginData) {
          setIsLoggedIn(true);
          setUserDetails(loginData.user);
          setToken(loginData.access_token);

          navigate({
            to: redirectTo,
          });
        } else {
          setErrMsg(`${provider} login failed`);
        }
      } catch (err: any) {
        console.error(`${provider} login error:`, err);
        if (err.response?.status === 409) {
          setErrMsg("User already exists");
        } else if ([401, 403].includes(err.response?.status)) {
          setErrMsg(
            err.response?.data?.error?.split(": ")[1]?.trim() ||
            `${provider} login failed`
          );
        } else {
          setErrMsg(`${provider} login failed. Please try again.`);
        }
        setIsLoggedIn(false);
      }
    },
    [setErrMsg, setIsLoggedIn, setUserDetails, navigate]
  );

  useEffect(() => {
    if (userDetails) {
      checkUserEvents();
    }
  }, [userDetails]);

  return {
    handleCheckIfLoggedIn,
    handleLogout,
    handleSignIn,
    handleOAuthSignIn,
  };
};
